import { CommonModule } from '@angular/common';
import { AuditDirectoryCellComponent } from '../../shared/audit/audit-directory-cell.component';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { finalize, forkJoin } from 'rxjs';
import { CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { companyOwnedWarehouses, isThirdPartyVirtualWarehouse, statusSeverity, warehousingBreadcrumb, warehousingHomeBreadcrumb } from '../warehousing-common';
import { WarehousingMasterData, WarehousingSearchFilters, WarehousingTransfer } from '../warehousing.model';
import { WarehousingPageHeaderComponent } from '../warehousing-page-header.component';
import { WarehousingSearchPanelComponent } from '../warehousing-search-panel.component';
import { WarehousingService } from '../warehousing.service';

@Component({
  selector: 'app-warehousing-transfer',
  standalone: true,
  imports: [AuditDirectoryCellComponent,
    CommonModule, ReactiveFormsModule, RouterModule, BreadcrumbModule, ButtonModule, IftaLabelModule,
    InputGroupModule, InputGroupAddonModule, InputTextModule, ProgressBarModule, SelectModule, TableModule,
    TagModule, ToastModule, WarehousingPageHeaderComponent, WarehousingSearchPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './warehousing-transfer.component.html'
})
export class WarehousingTransferComponent implements OnInit {
  private fb = inject(FormBuilder);
  private warehousingService = inject(WarehousingService);
  private crmService = inject(CrmService);
  private messageService = inject(MessageService);

  isLoading = false;
  isSubmitting = false;
  breadcrumbItems: MenuItem[] = warehousingBreadcrumb('Transfers');
  homeBreadcrumb: MenuItem = warehousingHomeBreadcrumb();
  transfers: WarehousingTransfer[] = [];
  clients: CrmClient[] = [];
  allWarehouses: WarehousingMasterData[] = [];
  warehouses: WarehousingMasterData[] = [];
  statusOptions = [
    { label: 'Planned', value: 'PLANNED' },
    { label: 'Reserved', value: 'RESERVED' },
    { label: 'Picked', value: 'PICKED' },
    { label: 'Staged', value: 'STAGED' },
    { label: 'Loaded', value: 'LOADED' },
    { label: 'In Transit', value: 'IN_TRANSIT' },
    { label: 'Partially Received', value: 'PARTIALLY_RECEIVED' },
    { label: 'Received', value: 'RECEIVED' },
    { label: 'Cancelled', value: 'CANCELLED' }
  ];

  searchForm = this.fb.group({
    keyword: [''],
    crmClientId: [null as number | null],
    sourceWarehouseId: [null as number | null],
    destinationWarehouseId: [null as number | null],
    status: [null as string | null],
    scheduledFrom: [''],
    scheduledTo: ['']
  });

  ngOnInit(): void {
    this.loadTransfers();
  }

  loadTransfers(): void {
    this.isLoading = true;
    forkJoin({
      transfers: this.warehousingService.getTransfers(this.searchFilters()),
      clients: this.crmService.getClients(),
      warehouses: this.warehousingService.getMasterData('WAREHOUSE')
    }).subscribe({
      next: response => {
        this.transfers = response.transfers.data || [];
        this.clients = response.clients || [];
        this.allWarehouses = response.warehouses.data || [];
        this.warehouses = companyOwnedWarehouses(this.allWarehouses);
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  applySearch(): void {
    this.loadTransfers();
  }

  clearSearch(): void {
    this.searchForm.reset({
      keyword: '',
      crmClientId: null,
      sourceWarehouseId: null,
      destinationWarehouseId: null,
      status: null,
      scheduledFrom: '',
      scheduledTo: ''
    });
    this.loadTransfers();
  }

  reserveTransfer(transfer: WarehousingTransfer): void {
    if (!transfer.id || this.isSubmitting) return;
    this.isSubmitting = true;
    this.warehousingService.reserveTransfer(transfer.id).pipe(finalize(() => this.isSubmitting = false)).subscribe(resp => {
      if (!resp.success) return;
      this.messageService.add({ severity: 'success', summary: 'Reserved', detail: 'Source stock units have been reserved.' });
      this.loadTransfers();
    });
  }

  cancelTransfer(transfer: WarehousingTransfer): void {
    if (!transfer.id || this.isSubmitting) return;
    this.isSubmitting = true;
    this.warehousingService.cancelTransfer(transfer.id).pipe(finalize(() => this.isSubmitting = false)).subscribe(resp => {
      if (!resp.success) return;
      this.messageService.add({ severity: 'success', summary: 'Cancelled', detail: 'Transfer order was cancelled.' });
      this.loadTransfers();
    });
  }

  clientName(id?: number): string {
    return this.clients.find(client => client.id === id)?.companyName || '-';
  }

  warehouseName(id?: number): string {
    return this.warehouses.find(row => row.id === id)?.name || '-';
  }

  private warehouseById(id?: number): WarehousingMasterData | undefined {
    return this.allWarehouses.find(row => row.id === id);
  }

  usesVirtualWarehouse(transfer: WarehousingTransfer): boolean {
    return isThirdPartyVirtualWarehouse(this.warehouseById(transfer.sourceWarehouseId))
      || isThirdPartyVirtualWarehouse(this.warehouseById(transfer.destinationWarehouseId));
  }

  quantitiesSummary(transfer: WarehousingTransfer): string {
    const lines = transfer.lines || [];
    const requested = lines.reduce((sum, line) => sum + (line.quantityRequested || 0), 0);
    const reserved = lines.reduce((sum, line) => sum + (line.quantityReserved || 0), 0);
    const dispatched = lines.reduce((sum, line) => sum + (line.quantityDispatched || 0), 0);
    const received = lines.reduce((sum, line) => sum + (line.quantityReceived || 0), 0);
    return `Req ${requested} | Res ${reserved} | Out ${dispatched} | In ${received}`;
  }

  canReserve(transfer: WarehousingTransfer): boolean {
    const lines = transfer.lines || [];
    const requested = lines.reduce((sum, line) => sum + (line.quantityRequested || 0), 0);
    const reserved = lines.reduce((sum, line) => sum + (line.quantityReserved || 0), 0);
    return !this.usesVirtualWarehouse(transfer)
      && (transfer.status || '').toUpperCase() === 'PLANNED'
      && reserved < requested;
  }

  canContinue(transfer: WarehousingTransfer): boolean {
    return !this.usesVirtualWarehouse(transfer)
      && ['RESERVED', 'PICKED', 'STAGED', 'LOADED'].includes((transfer.status || '').toUpperCase());
  }

  canCancel(transfer: WarehousingTransfer): boolean {
    return !['IN_TRANSIT', 'PARTIALLY_RECEIVED', 'RECEIVED', 'CANCELLED'].includes((transfer.status || '').toUpperCase());
  }

  statusSeverity = statusSeverity;

  private searchFilters(): WarehousingSearchFilters {
    return this.searchForm.getRawValue();
  }
}
